import entityData from './assets/entityData.json';

// Game constants
export let TILE_SIZE = 64; // 32 pixels = 1 meter
export let TILE_SCALE = 1; // Multiplier for rendering tiles (1 = normal size)
export let PLAYER_SIZE = TILE_SIZE * TILE_SCALE * 1.0; // Player is same size as tile (rendered)
export let RENDER_RADIUS = 8; // Increased from 2 to 20 for better visibility
export let SUB_TILE_SIZE = 8; // Size of sub-tile divisions
export let ENTITY_BASE_RADIUS = TILE_SIZE * 0.5; // Base radius for entities (half tile size)

// Player stats configuration
export const PLAYER_STATS = {
    // Initial values
    INITIAL_HEALTH: 100,
    INITIAL_HUNGER: 100,
    INITIAL_THIRST: 100,
    INITIAL_WETNESS: 0,

    // Maximum values
    MAX_HEALTH: 100,
    MAX_HUNGER: 100,
    MAX_THIRST: 100,
    MAX_WETNESS: 100,

    // Minimum values (death thresholds)
    MIN_HEALTH: 0,
    MIN_HUNGER: 0,
    MIN_THIRST: 0,

    // Survival time configuration (in days)
    DAYS_WITHOUT_FOOD: 14,   // Can survive 14 days without food (2 weeks)
    DAYS_WITHOUT_WATER: 3,   // Can survive 3 days without water

    // Calculated rates (DO NOT modify these directly - change the DAYS values above)
    // These values represent how much is drained per second to reach 0 in the specified days
    get HUNGER_RATE() { 
        // 100% hunger / (days * 24 hours * 60 minutes * 60 seconds) = depletion per second
        return this.MAX_HUNGER / (this.DAYS_WITHOUT_FOOD * 24 * 60 * 60);
    },
    get THIRST_RATE() { 
        return this.MAX_THIRST / (this.DAYS_WITHOUT_WATER * 24 * 60 * 60);
    },

    // Legacy rates - kept for backward compatibility
    // NOTE: These are overridden by the getters above
    HUNGER_RATE: 0.0001,   // How quickly hunger decreases (reduced from 0.001)
    THIRST_RATE: 0.0003,   // How quickly thirst decreases (reduced from 0.0015)
    
    WETNESS_RATE: 0.2,     // How quickly wetness increases when in water
    DRY_RATE: 0.1,         // How quickly wetness decreases when not in water

    // Damage rates when stats are low
    HUNGER_DAMAGE_RATE: 0.05,  // Health damage per second when hunger is 0
    THIRST_DAMAGE_RATE: 0.10,  // Health damage per second when thirst is 0

    // Recovery rates
    HEALTH_RECOVERY_RATE: 0.2,  // Health recovery per second when well-fed and hydrated
    HUNGER_RECOVERY_RATE: 5.0,  // Hunger recovery per food item
    THIRST_RECOVERY_RATE: 10.0, // Thirst recovery per water item
};

// Player movement constants
export let PLAYER_SPEED = 0.5;
export let PLAYER_ACCELERATION = 0.1;
export let PLAYER_DECELERATION = 0.15;
export let PLAYER_MAX_SPEED = 2.0;

// Map size presets
export const MAP_SIZES = {
    MINI: { width: 7, height: 7, name: "Mini Island" },
    TINY: { width: 20, height: 20, name: "Tiny Island" },
    SMALL: { width: 50, height: 50, name: "Small Island" },
    MEDIUM: { width: 400, height: 400, name: "Medium Island" },
    LARGE: { width: 800, height: 800, name: "Large Island" },
    HUGE: { width: 1000, height: 1000, name: "Huge Island" },
    GIGANTIC: { width: 2000, height: 2000, name: "Gigantic Island" },
    REALISTIC: { width: 10000, height: 10000, name: "Realistic Island" },
    RECTANGULAR_SMALL: { width: 600, height: 400, name: "Small Rectangular Island" },
    RECTANGULAR_LARGE: { width: 800, height: 600, name: "Large Rectangular Island" },
};

// Current map configuration - change MAP_PRESET to use different sizes
export let MAP_PRESET = MAP_SIZES.SMALL; // Default to MEDIUM preset
export let MAP_WIDTH = MAP_PRESET.width;
export let MAP_HEIGHT = MAP_PRESET.height;

// Reference size and scaling calculations
export const BASE_MAP_SIZE = 400; // Reference size for scaling calculations
export let MAP_SCALE = Math.sqrt((MAP_WIDTH * MAP_HEIGHT) / (BASE_MAP_SIZE * BASE_MAP_SIZE));

// Scaled constants based on map size
export let ISLAND_BASE_RADIUS = Math.min(MAP_WIDTH, MAP_HEIGHT) / 3;
export let NOISE_SCALE = 8 / Math.max(MAP_WIDTH, MAP_HEIGHT) * 3;
export let NOISE_MULTIPLIER = Math.sqrt(MAP_WIDTH * MAP_HEIGHT) / 20 * 2.5;
export let BIOME_SCALE = MAP_SCALE;

// ECS constants
export const MAX_ENTITIES = 10000000; // Maximum number of entities in the world

// Simulation timing
// SPAWN_TIME is the in-game hour (0-23) at which spawning occurs, not a duration in seconds
export const SPAWN_TIME = 8; // _ o'clock (_ AM) in-game time for spawn events
export let TIME_SCALE = 100.0; // Simulation time scale (1.0 = real time, >1 = faster, <1 = slower)

// Add after TILE_SIZE:
export let CHUNK_SIZE = 4; // Number of tiles per chunk
export let CHUNKS_IN_VIEW = 1; // Number of chunks to render in each direction from player 

export let LIGHTING_RADIUS = 20; // Radius of the visible area at night in pixels 

// Lighting settings
export let NIGHT_LIGHTING_RADIUS = TILE_SIZE * 20; // Radius of the visible area at night in pixels
export let DAY_LIGHTING_RADIUS = TILE_SIZE * 20; // Radius of the visible area during day in pixels

// Game modes
export const GAME_MODES = {
    FULL: {
        time: true,
        weather: true,
        animals: true,
        plants: true,
        lighting: true,
    },
    FUNC: {
        time: false,
        weather: false,
        animals: false,
        plants: true,
        lighting: false,
    },
    FROZEN: {
        time: false, // No time system, world is frozen
        weather: false,
        animals: false,
        plants: true,
        lighting: false,
    },
    TILES_ONLY: {
        time: false,
        weather: false,
        animals: false,
        plants: false,
        lighting: false,
    },
    MINIMAL: {
        time: false,
        weather: false,
        animals: false,
        plants: false,
        lighting: false,
    }
};

// Current game mode - using a mutable object
export let CURRENT_MODE = { ...GAME_MODES.FULL };
CURRENT_MODE.animals = true;  // <-- Enable animals (for rabbits)
CURRENT_MODE.hives = false;   // <-- Temporarily disable hives
CURRENT_MODE.bees = false;    // <-- Temporarily disable bees

// Entity types registry - define all entity categories and known entity types
export const ENTITY_REGISTRY = {
    animals: {
        types: Object.keys(entityData.ANIMAL_TYPES),
        component: "Animal" // Component name for this entity category
    },
    plants: {
        types: Object.keys(entityData.PLANT_TYPES),
        component: "Plant"
    },
    rocks: {
        types: [
            "SMALL",     // Small rocks
            "MEDIUM",    // Medium rocks
            "LARGE"      // Large rocks
        ],
        component: "Rock"
    },
    structures: {
        types: [
            "HIVE", "NEST", "DEN", "BURROW"
        ],
        component: "Structure"
    }
};

// Entity spawning configuration - control which entity types can spawn using regex patterns
export const ENTITY_SPAWN_CONFIG = {
    // Global settings that apply to all entity categories
    global: {
        // Global patterns that apply to all entities regardless of category
        enabledTypes: [
            ".*" // By default, allow all entities
        ],
        disabledTypes: [
            ".*_BOSS$",      // Prevent boss variants from spawning
            "DANGEROUS_.*",  // Prevent dangerous variants
            "RARE_.*",       // Prevent rare variants by default
            "DEBUG_.*"       // Prevent debug entities
        ],
        defaultMaxCount: 10  // Default maximum if not specified elsewhere
    },
    
    // Animal-specific settings
    animals: {
        enabledTypes: [
            "RABBIT.*",    // Allow all rabbit variants
            "CHICKEN",     // Allow chickens
            "^FISH",       // Allow any fish types
            "DEER",        // Allow deer
            "WOLF",        // Allow wolves
            "BEE_EATER",    // Allow bee eaters
            "GOAT",         // Allow goats
            "HAWK"          // Allow hawks
        ],
        disabledTypes: [
            ".*_PREDATOR$" // Prevent predator variants
        ],
        maxCounts: {
            "RABBIT": 20,     // Maximum 20 rabbits
            "CHICKEN": 15,    // Maximum 15 chickens
            "FISH": 30,       // Maximum 30 fish
            "WOLF": 5,        // Maximum 5 wolves
            "BEE_EATER": 2,   // Maximum 2 bee eaters
            "GOAT": 10,        // Maximum 10 goats
            "HAWK": 1         // Maximum 1 hawk
        },
        defaultMaxCount: 15   // Default maximum for animals if not specified above
    },
    
    // Plant-specific settings
    plants: {
        enabledTypes: [
            "TREE.*",      // Allow all tree variants
            "FLOWER.*",    // Allow all flower variants
            "MUSHROOM.*",  // Allow all mushroom variants
            "HERB.*"       // Allow all herbs
        ],
        disabledTypes: [
            ".*_POISONOUS" // Prevent poisonous plants by default
        ],
        maxCounts: {
            "TREE": 50,       // Maximum 50 trees
            "FLOWER": 100,    // Maximum 100 flowers
            "MUSHROOM": 30,   // Maximum 30 mushrooms
            "HERB": 40        // Maximum 40 herbs
        },
        defaultMaxCount: 80   // Default maximum for plants if not specified
    },
    
    // Rock-specific settings
    rocks: {
        enabledTypes: [
            "SMALL",     // Small rocks
            "MEDIUM",    // Medium rocks
            "LARGE"      // Large rocks
        ],
        maxCounts: {
            "SMALL": 60,      // Maximum 60 small rocks
            "MEDIUM": 20,     // Maximum 20 medium rocks
            "LARGE": 20        // Maximum 20 large rocks
        },
        defaultMaxCount: 30   // Default maximum for rocks
    },
    
    // Structure-specific settings
    structures: {
        enabledTypes: [
            "HIVE",        // Allow beehives
            "NEST",        // Allow nests
            "DEN"          // Allow animal dens
        ],
        maxCounts: {
            "HIVE": 3,        // Maximum 3 beehives
            "NEST": 10,       // Maximum 10 nests
            "DEN": 5          // Maximum 5 dens
        },
        defaultMaxCount: 5    // Default maximum for structures
    }
};

// Track current entity counts - updated by entity systems
export const ENTITY_COUNTS = {
    animals: {},
    plants: {},
    rocks: {},
    structures: {}
};

// Function to update game mode
export function updateGameMode(newMode) {
    CURRENT_MODE = { ...newMode };
    // Dispatch event to notify systems of mode change
    window.dispatchEvent(new CustomEvent('gameModeChanged', { detail: CURRENT_MODE }));
}

// Function to update config values
export function updateConfig(name, value) {
    switch(name) {
        case 'TILE_SIZE':
            TILE_SIZE = value;
            // Update dependent values
            PLAYER_SIZE = TILE_SIZE * TILE_SCALE * 1.0;
            ENTITY_BASE_RADIUS = TILE_SIZE * 0.5;
            NIGHT_LIGHTING_RADIUS = TILE_SIZE * 20;
            DAY_LIGHTING_RADIUS = TILE_SIZE * 20;
            break;
        case 'TILE_SCALE':
            TILE_SCALE = value;
            // Update dependent values
            PLAYER_SIZE = TILE_SIZE * TILE_SCALE * 1.0;
            break;
        case 'PLAYER_SIZE':
            PLAYER_SIZE = value;
            break;
        case 'RENDER_RADIUS':
            RENDER_RADIUS = value;
            break;
        case 'SUB_TILE_SIZE':
            SUB_TILE_SIZE = value;
            break;
        case 'ENTITY_BASE_RADIUS':
            ENTITY_BASE_RADIUS = value;
            break;
        case 'PLAYER_SPEED':
            PLAYER_SPEED = value;
            break;
        case 'PLAYER_ACCELERATION':
            PLAYER_ACCELERATION = value;
            break;
        case 'PLAYER_DECELERATION':
            PLAYER_DECELERATION = value;
            break;
        case 'PLAYER_MAX_SPEED':
            PLAYER_MAX_SPEED = value;
            break;
        case 'MAP_WIDTH':
            MAP_WIDTH = value;
            // Update dependent values
            MAP_SCALE = Math.sqrt((MAP_WIDTH * MAP_HEIGHT) / (BASE_MAP_SIZE * BASE_MAP_SIZE));
            ISLAND_BASE_RADIUS = Math.min(MAP_WIDTH, MAP_HEIGHT) / 3;
            NOISE_SCALE = 8 / Math.max(MAP_WIDTH, MAP_HEIGHT) * 3;
            NOISE_MULTIPLIER = Math.sqrt(MAP_WIDTH * MAP_HEIGHT) / 20 * 2.5;
            BIOME_SCALE = MAP_SCALE;
            break;
        case 'MAP_HEIGHT':
            MAP_HEIGHT = value;
            // Update dependent values
            MAP_SCALE = Math.sqrt((MAP_WIDTH * MAP_HEIGHT) / (BASE_MAP_SIZE * BASE_MAP_SIZE));
            ISLAND_BASE_RADIUS = Math.min(MAP_WIDTH, MAP_HEIGHT) / 3;
            NOISE_SCALE = 8 / Math.max(MAP_WIDTH, MAP_HEIGHT) * 3;
            NOISE_MULTIPLIER = Math.sqrt(MAP_WIDTH * MAP_HEIGHT) / 20 * 2.5;
            BIOME_SCALE = MAP_SCALE;
            break;
        case 'CHUNK_SIZE':
            CHUNK_SIZE = value;
            break;
        case 'CHUNKS_IN_VIEW':
            CHUNKS_IN_VIEW = value;
            break;
        case 'LIGHTING_RADIUS':
            LIGHTING_RADIUS = value;
            break;
        case 'NIGHT_LIGHTING_RADIUS':
            NIGHT_LIGHTING_RADIUS = value;
            break;
        case 'DAY_LIGHTING_RADIUS':
            DAY_LIGHTING_RADIUS = value;
            break;
        case 'TIME_SCALE':
            TIME_SCALE = value;
            break;
    }
    // Dispatch event to notify systems of config change
    window.dispatchEvent(new CustomEvent('configChanged', { detail: { name, value } }));
} 